import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GoogleAuthPage = () => {
  const { language } = useLanguage();
  const { setUserFromToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error

  useEffect(() => {
    const handleGoogleCallback = async () => {
      const token = searchParams.get('token');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        toast.error(language === 'fr' ? 'Erreur de connexion Google' : 'Google sign-in failed');
        setTimeout(() => navigate('/auth'), 2000);
        return;
      }

      if (token) {
        try {
          // Save token and update auth state
          localStorage.setItem('roomia_token', token);
          await setUserFromToken(token);
          
          setStatus('success');
          toast.success(language === 'fr' ? 'Connexion réussie !' : 'Successfully signed in!');
          setTimeout(() => navigate('/dashboard'), 1500);
        } catch (err) {
          console.error('Error processing Google auth:', err);
          setStatus('error');
          toast.error(language === 'fr' ? 'Erreur lors de la connexion' : 'Error during sign-in');
          setTimeout(() => navigate('/auth'), 2000);
        }
      } else {
        // No token, redirect to auth
        navigate('/auth');
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate, language, setUserFromToken]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="Roomia.net" 
              className="h-16 w-auto"
            />
          </div>

          <div className="text-center py-8">
            {status === 'processing' && (
              <>
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                <h2 className="font-heading text-xl font-bold mb-2">
                  {language === 'fr' ? 'Connexion en cours...' : 'Signing in...'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {language === 'fr' 
                    ? 'Veuillez patienter pendant que nous vous connectons'
                    : 'Please wait while we sign you in'}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h2 className="font-heading text-xl font-bold mb-2 text-green-600">
                  {language === 'fr' ? 'Connexion réussie !' : 'Successfully signed in!'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {language === 'fr' 
                    ? 'Redirection vers votre tableau de bord...'
                    : 'Redirecting to your dashboard...'}
                </p>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h2 className="font-heading text-xl font-bold mb-2 text-red-600">
                  {language === 'fr' ? 'Erreur de connexion' : 'Sign-in failed'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {language === 'fr' 
                    ? 'Redirection vers la page de connexion...'
                    : 'Redirecting to login page...'}
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleAuthPage;
